import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Loader2, Save, X } from 'lucide-react'
import { apiService } from '@/services/api'
import { toast } from 'sonner'
import { useBlogMetadataContext } from '@/contexts/BlogMetadataContext'
import { BlogEditor } from './editor/BlogEditor'
import { imageUploadHandler } from './editor/ImageUploadHandler'

interface BlogTemplate {
  id: string
  name: string
  description?: string
  title_template?: string
  content_template: string
  excerpt_template?: string
  categories: Array<{ id: string; name: string; color?: string }>
  tags: Array<{ id: string; name: string }>
}

interface BlogTemplateFormDrawerProps {
  template?: BlogTemplate
  onClose: () => void
  onSuccess: () => void
}

export const BlogTemplateFormDrawer: React.FC<BlogTemplateFormDrawerProps> = ({
  template,
  onClose,
  onSuccess,
}) => {
  const { categories: allCategories, tags: allTags } = useBlogMetadataContext()
  const [loading, setLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const [formData, setFormData] = useState({
    name: template?.name || '',
    description: template?.description || '',
    title_template: template?.title_template || '',
    content_template: template?.content_template || '',
    excerpt_template: template?.excerpt_template || '',
    category_ids: template?.categories?.map(c => c.id) || [] as string[],
    tag_ids: template?.tags?.map(t => t.id) || [] as string[],
  })

  useEffect(() => {
    imageUploadHandler.createBucketIfNotExists()
  }, [])

  const handleSubmit = async () => {
    const errors: Record<string, string> = {}

    if (!formData.name.trim()) {
      errors.name = 'Template name is required'
    }

    if (!formData.content_template.trim()) {
      errors.content_template = 'Content template is required'
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      toast.error('Please fix validation errors before saving')
      return
    }

    setValidationErrors({})
    setLoading(true)

    try {
      if (template) {
        await apiService.blog.templates.update(template.id, {
          name: formData.name,
          description: formData.description || undefined,
          title_template: formData.title_template || undefined,
          content_template: formData.content_template,
          excerpt_template: formData.excerpt_template || undefined,
          category_ids: formData.category_ids,
          tag_ids: formData.tag_ids,
        })
        toast.success('Template updated successfully')
      } else {
        await apiService.blog.templates.create({
          name: formData.name,
          description: formData.description || undefined,
          title_template: formData.title_template || undefined,
          content_template: formData.content_template,
          excerpt_template: formData.excerpt_template || undefined,
          category_ids: formData.category_ids,
          tag_ids: formData.tag_ids,
        })
        toast.success('Template created successfully')
      }

      onSuccess()
    } catch (err) {
      console.error('Failed to save template:', err)
      toast.error('Failed to save template')
    } finally {
      setLoading(false)
    }
  }

  const toggleCategory = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }))
  }

  const toggleTag = (tagId: string) => {
    setFormData(prev => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter(id => id !== tagId)
        : [...prev.tag_ids, tagId]
    }))
  }

  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const url = await imageUploadHandler.uploadImage(file)
      toast.success('Image uploaded successfully')
      return url
    } catch (error) {
      toast.error('Failed to upload image')
      throw error
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <Label htmlFor="name" className={validationErrors.name ? 'text-red-600' : ''}>
            Template Name *
          </Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value })
              if (validationErrors.name) {
                setValidationErrors(prev => ({ ...prev, name: '' }))
              }
            }}
            placeholder="e.g., Product Announcement, How-To Guide"
            disabled={loading}
            className={validationErrors.name ? 'border-red-500' : ''}
          />
          {validationErrors.name && (
            <p className="text-sm text-red-600 mt-1">{validationErrors.name}</p>
          )}
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of this template's purpose"
            rows={2}
            disabled={loading}
          />
        </div>

        <div>
          <Label htmlFor="title_template">Title Template</Label>
          <Input
            id="title_template"
            value={formData.title_template}
            onChange={(e) => setFormData({ ...formData, title_template: e.target.value })}
            placeholder="e.g., How to [Topic]: A Complete Guide"
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Use [placeholders] for dynamic content
          </p>
        </div>

        <div>
          <Label htmlFor="excerpt_template">Excerpt Template</Label>
          <Textarea
            id="excerpt_template"
            value={formData.excerpt_template}
            onChange={(e) => setFormData({ ...formData, excerpt_template: e.target.value })}
            placeholder="Brief summary template with [placeholders]"
            rows={2}
            disabled={loading}
          />
        </div>

        <div>
          <Label className={validationErrors.content_template ? 'text-red-600' : ''}>
            Content Template *
          </Label>
          <div className="mt-2">
            <BlogEditor
              content={formData.content_template}
              onChange={(content) => {
                setFormData({ ...formData, content_template: content })
                if (validationErrors.content_template) {
                  setValidationErrors(prev => ({ ...prev, content_template: '' }))
                }
              }}
              onImageUpload={handleImageUpload}
              disabled={loading}
            />
          </div>
          {validationErrors.content_template && (
            <p className="text-sm text-red-600 mt-1">{validationErrors.content_template}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Create the structure and content placeholders for this template
          </p>
        </div>

        <div>
          <Label>Default Categories</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {allCategories.map((category) => (
              <Badge
                key={category.id}
                variant={formData.category_ids.includes(category.id) ? 'default' : 'outline'}
                className="cursor-pointer"
                style={category.color ? { borderColor: category.color } : {}}
                onClick={() => toggleCategory(category.id)}
              >
                {category.name}
                {formData.category_ids.includes(category.id) && (
                  <X className="ml-1 h-3 w-3" />
                )}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Select default categories for posts created with this template
          </p>
        </div>

        <div>
          <Label>Default Tags</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {allTags.map((tag) => (
              <Badge
                key={tag.id}
                variant={formData.tag_ids.includes(tag.id) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleTag(tag.id)}
              >
                {tag.name}
                {formData.tag_ids.includes(tag.id) && (
                  <X className="ml-1 h-3 w-3" />
                )}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Select default tags for posts created with this template
          </p>
        </div>
      </div>

      <div className="border-t border-border p-4 flex items-center justify-between bg-background">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Save className="mr-2 h-4 w-4" />
          {template ? 'Update Template' : 'Create Template'}
        </Button>
      </div>
    </>
  )
}
